<script setup lang="ts">
import { computed, nextTick, onMounted, onBeforeUnmount, ref, watch } from "vue";
import CustomerSearchDialog from "./CustomerSearchDialog.vue";
import CheckoutModal from "./CheckoutModal.vue";
import FreeItemChooserModal from "./FreeItemChooserModal.vue";
import { posStore } from "../../../../../stores/posStore";
import AsyncImage from "../../../../../components/ui/form/AsyncImage.vue";
import {
  formatCurrency,
  formatNumber,
  isEmpty,
  printPDFBlob,
} from "../../../../../utils/CommonFunctions";
import IconComponent from "../../../../../components/ui/common/IconComponent.vue";
import NumberInput from "../../../../../components/ui/form/NumberInput.vue";
import { api } from "../../../../../api/Api";
import Toast from "../../../../../services/Toast";
import {
  CashierDuty,
  Customer,
  DeliveryTracking,
  Item,
  ItemCategory,
  ItemGroup,
  PaymentType,
  PosStation,
  RecordStatus,
  RestaurantTables,
  SalesDocument,
  SalesRep,
} from "../../../../../types/models";
import DropDownButton from "../../../../../components/ui/common/DropdownButton.vue";
import { User } from "../../../../../types/auth";
import MessageBox from "../../../../../services/MessageBox";
import SearchSelect from "../../../../../components/ui/form/SearchSelect.vue";
import CustomersForm from "../../../masterdata/customers/CustomersForm.vue";
import CustomerAddressForm from "../../../masterdata/customers/address/CustomerAddressForm.vue";
import Buttons from "../../../../../components/ui/form/Buttons.vue";
import DataTablePagination from "../../../../../components/table/DataTablePagination.vue";
import DeliveryDetails from "../../deliverytrackings/DelieryDetails.vue";
import { useNotificationsStore } from "../../../../../stores/notifications";
import type { PosNotification } from "../../../../../stores/notifications";
import { useRouter } from "vue-router";

const props = defineProps<{
  payment_types: PaymentType[];
  item_categories: ItemCategory[];
  item_groups: ItemGroup[];
  delivery_staffs: User[];
  posStation: PosStation | undefined;
  cashierDuty: CashierDuty | null;
  salesReps: SalesRep[];
}>();

const emit = defineEmits<{
  (e: "new"): void;
  (e: "back"): void;
  (e: "order", table: RestaurantTables): void;
  (e: "order-created", tables: RestaurantTables[]): void;
  (e: "view-bill", table: RestaurantTables): void;
}>();

const pos = posStore();
const activeTab = ref("order");
//const customerSearch = ref("");
const showCustomerList = ref(false);
const customerList = ref([]);
const pendingDeliveries = ref<DeliveryTracking[]>([]);
const endPoint = "revenue/sales/pos/";

/**
 * Run the server-side promotion preview, then open the payment modal. The
 * preview mutates each cart line so the totals the cashier sees on the
 * payment screen already include any applicable promo discount.
 *
 * If any free-line promo has a multi-item giveaway pool, the chooser modal
 * is opened FIRST (one per pool, queued sequentially) so the cashier must
 * pick which items + qty to give before the payment screen appears. The
 * cashier MAY give fewer free units than earned but never more — the
 * server validates and falls back to default on any mismatch.
 *
 * Safe to call when no promos apply — the server returns a zero-discount
 * result and the cart is left unchanged.
 */
const openCheckout = async () => {
  if ((pos.selectedPosInvoice?.invoice.items?.length ?? 0) === 0) return;
  await pos.previewPromotion();
  if (choosablePools.value.length > 0) {
    openAutoChooserFlow(choosablePools.value);
    return;
  }
  pos.showCheckoutModal = true;
};

/**
 * Free-line outcomes posStore.previewPromotion() stashed on the invoice.
 * Templates can't apply TS `as any` casts, so we narrow once here and
 * expose a typed list (defaulting to [] when no promo previewed yet).
 */
type FreeLine = { items_id: number; qty: number; promo_name: string; promo_uid?: string };
const freeLines = computed<FreeLine[]>(() => {
  const fl = (pos.selectedPosInvoice?.invoice as any)?.free_lines;
  return Array.isArray(fl) ? fl : [];
});

// Multi-item giveaway pools — when a promo's eligible pool has >1 item,
// the cashier should be able to swap or split the freebie. Each pool is
// per-promo and surfaces alongside `free_lines` from the preview.
type PoolItem = {
  items_id: number;
  code: string;
  name: string;
  sales_uom: string;
  sales_uom_qty: number;
  on_hand: number;
};
type FreePool = {
  promo_uid: string;
  promo_name: string;
  free_total_qty: number;
  free_uom_id: number | null;
  items: PoolItem[];
  expense_account_id: number | null;
};
type ChosenLine = { promo_uid: string; items_id: number; qty: number };
const freePools = computed<FreePool[]>(() => {
  const fp = (pos.selectedPosInvoice?.invoice as any)?.free_pools;
  return Array.isArray(fp) ? fp : [];
});
const choosablePools = computed<FreePool[]>(() =>
  freePools.value.filter((p) => (p.items?.length ?? 0) > 1)
);
const freeLinesOverride = computed<ChosenLine[]>(() => {
  const o = (pos.selectedPosInvoice?.invoice as any)?.free_lines_override;
  return Array.isArray(o) ? o : [];
});

const activeChooserPool = ref<FreePool | null>(null);
// Queue of pools still to be chosen. `chooserDestination` records what
// should happen once the queue is empty:
//   'checkout' — auto-flow from Checkout click → open CheckoutModal next
//   null       — manual amber-button open → just close the modal
const pendingPoolQueue = ref<FreePool[]>([]);
const chooserDestination = ref<"checkout" | null>(null);

const openNextChooser = () => {
  if (pendingPoolQueue.value.length > 0) {
    activeChooserPool.value = pendingPoolQueue.value.shift() ?? null;
    return;
  }
  activeChooserPool.value = null;
  if (chooserDestination.value === "checkout") {
    pos.showCheckoutModal = true;
  }
  chooserDestination.value = null;
};

const openChooser = (pool: FreePool) => {
  // Manual open from the amber "Choose" button — no checkout destination.
  pendingPoolQueue.value = [pool];
  chooserDestination.value = null;
  openNextChooser();
};
const openAutoChooserFlow = (pools: FreePool[]) => {
  // Triggered by openCheckout: queue every pool and chain through them
  // before opening CheckoutModal. Cancel anywhere aborts the whole flow.
  pendingPoolQueue.value = [...pools];
  chooserDestination.value = "checkout";
  openNextChooser();
};
const closeChooser = () => {
  // Cancel aborts the entire chooser flow — checkout does NOT proceed.
  pendingPoolQueue.value = [];
  activeChooserPool.value = null;
  chooserDestination.value = null;
};
const applyChooser = (lines: ChosenLine[]) => {
  const inv: any = pos.selectedPosInvoice?.invoice;
  if (!inv) {
    closeChooser();
    return;
  }
  const others = (inv.free_lines_override ?? []).filter(
    (l: ChosenLine) => l.promo_uid !== activeChooserPool.value?.promo_uid
  );
  inv.free_lines_override = [...others, ...lines];
  // Mirror onto the cashier-facing `free_lines` so the "Free items (give to
  // customer)" hint reflects the new choice without a re-preview round-trip.
  const otherFL = (inv.free_lines ?? []).filter(
    (l: FreeLine) => l.promo_uid !== activeChooserPool.value?.promo_uid
  );
  const newFL = lines.map((l) => ({
    items_id: l.items_id,
    qty: l.qty,
    promo_name: activeChooserPool.value?.promo_name ?? "",
    promo_uid: l.promo_uid,
  }));
  inv.free_lines = [...otherFL, ...newFL];
  // Advance to the next pool (auto-flow) or open CheckoutModal / close.
  openNextChooser();
};

defineExpose({
  triggerCheckout() {
    if (
      !pos.showCheckoutModal &&
      (pos.selectedPosInvoice?.invoice.items?.length ?? 0) > 0
    ) {
      openCheckout();
    }
  },
});

const tabs = ref([
  { id: "order", label: props.posStation?.outlet?.type == 'Shop' ? "Current Order" : 'Items', icon: "ShoppingCart", visible: true },
  { id: "deliveries", label: "Deliveries", icon: "Truck", badge: "", visible: props.posStation?.outlet?.type == 'Shop' },
  { id: "notifications", label: "Notifications", icon: "Bell", badge: "", visible: props.posStation?.outlet?.type == 'Shop' },
]);

// Sample data for pending deliveries

/* ───────────────────  Notifications panel  ───────────────────
 * Pulls from the same Pinia inbox the header bell + drawer use, so the POS
 * panel, drawer, and full-page tab all stay in sync. Pre-loads on mount in
 * case the cashier opens the tab before the bell has had a chance to
 * hydrate, then leans on the bell's polling loop to keep things current.
 */
const notificationsStore = useNotificationsStore();
const router = useRouter();

const notifications = computed<PosNotification[]>(() =>
  notificationsStore.items.filter((n) => !n.dismissed_at),
);

onMounted(() => {
  if (!notificationsStore.initialLoaded) {
    notificationsStore.load();
  }
});

// Live-sync the Notifications tab badge with the unread count so the cashier
// sees a number bubble on the tab the same way the header bell shows one.
watch(
  () => notificationsStore.unreadCount,
  (n) => {
    const tab = tabs.value.find((t) => t.id === "notifications");
    if (tab) tab.badge = n > 0 ? String(n) : "";
  },
  { immediate: true },
);

function notificationIcon(severity: string): string {
  switch (severity) {
    case "urgent":
    case "error":
      return "🚨";
    case "warning":
      return "⚠️";
    case "success":
      return "✅";
    default:
      return "🔔";
  }
}

function notificationTimeAgo(iso: string | null): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

async function openNotification(n: PosNotification) {
  if (!n.read_at) await notificationsStore.markRead(n.id);
  if (!n.link) return;
  // Deep-link convention: `sales-order:UID` opens the order in a modal on
  // the POS Screen (POSListView watches notificationsStore.pendingSalesOrderUid
  // and fetches via clients/revenue/sales/sales-orders/record). Already
  // inside the POS shell — no navigation needed.
  if (n.link.startsWith("sales-order:")) {
    notificationsStore.openSalesOrder(n.link.slice("sales-order:".length));
    return;
  }
  router.push(n.link);
}

const showCustomerForm = ref(false);
const handleNoCustomer = () => {
  if (pos.customerSearch.length >= 3) {
    MessageBox.confirm({
      title: "No Customer Found",
      message: "Do you want to create a new customer?",
      confirmText: "Create New Customer",
      cancelText: "Cancel",
      type: "info",
    }).then((result) => {
      if (result) {
        customerMode.value = "create";
        showCustomerForm.value = true;
      }
    });
  } else {
    Toast.show("Please enter at least 3 characters to search for a customer", "warning");
  }
};

const handleCustomerCreated = (data: any) => {  
  if (customerMode.value === "edit") {
    pos.setCustomer(data);    
  } else {
    const mob = data.mobile.split('-')[1];
    pos.customerSearch = mob;
    handleCustomerSearch();
  }
 nextTick(() => {
     showCustomerForm.value = false;
  pos.customerSearch = "";
  });
}
const handleCustomerSearch = async () => {
  if (pos.customerSearch.length >= 3) {
    const response = await api.post(endPoint + "customers", {
      search_query: pos.customerSearch,
      details: true,
    });
    if (response.success == true) {
      if (response.data.length > 1) {
        customerList.value = response.data;
        showCustomerList.value = true;
      } else if (response.data.length == 1) {
        pos.setCustomer(response.data[0]);
      } else {
        handleNoCustomer();
      }
    } else {
      Toast.show("Could not retrieve customers", "warning");
    }
  }
};

/**
 * State / city cascade.
 *
 *  - On state change: clear any prior city filter, refetch cities scoped to
 *    the new state (or clear them when the state itself is cleared), and
 *    reload the deliveries list with the updated filter.
 *  - On city change: just refresh deliveries — the state stays intact (the
 *    earlier behaviour cleared `states_id` here, which made cities behave
 *    like a top-level filter and silently invalidated the cascade).
 */
const handleStateChanged = async () => {
  delivery_citys_id.value = "";
  const stateId = String(delviery_states_id.value ?? "");
  if (!stateId) {
    citys.value = [];
  } else {
    await loadCities(stateId);
  }
  getPendingDeliveries();
};
const handleCityChanged = () => {
  getPendingDeliveries();
};

/** Load the full states list from the master-data endpoint once on mount.
 *  Cached in `states` (the SearchSelect's `:options`). Silent on failure —
 *  the cashier can still operate on the deliveries list without filters. */
const loadStates = async () => {
  try {
    const response: any = await api.post(
      "clients/system/master-data/states/list",
      {},
      { skipLoading: true } as any,
    );
    if (response?.success && Array.isArray(response.data)) {
      states.value = response.data.map((s: any) => ({ id: s.id, name: s.name }));
    }
  } catch {
    /* best-effort — see comment above */
  }
};

/** Load cities scoped to a chosen `states_id`. CitysController's `getList`
 *  reads the filter from the `extra` query param. */
const loadCities = async (statesId: string | number) => {
  try {
    const response: any = await api.post(
      "clients/system/master-data/citys/list",
      { extra: statesId },
      { skipLoading: true } as any,
    );
    if (response?.success && Array.isArray(response.data)) {
      citys.value = response.data.map((c: any) => ({ id: c.id, name: c.name }));
    } else {
      citys.value = [];
    }
  } catch {
    citys.value = [];
  }
};

const deliveryPageChange = (page: number) => {
  deliveryCurrentPage.value = page;
  getPendingDeliveries();
};

const refreshDeliverys = () => {
  deliveryCurrentPage.value = 1;
  getPendingDeliveries();
};
/**
 * Pending deliveries fetcher. Used both by the manual filter handlers AND
 * by the 5-minute polling loop. The polling loop passes `silent=true` so
 * any transient failure (warehouse offline, browser napping) stays out of
 * the cashier's face — only manual clicks deserve a Toast.
 *
 * When `silent=false` we ALSO compare the freshly-loaded ids against the
 * `seenDeliveryIds` set captured on the previous fetch and push a heads-up
 * bubble per truly-new delivery — that's how the polling loop surfaces a
 * just-created delivery to a cashier deep in another tab.
 */
const seenDeliveryIds = ref<Set<number>>(new Set());
const deliveriesInitialised = ref(false);

const getPendingDeliveries = async (opts: { silent?: boolean } = {}) => {
  let response: any;
  try {
    response = await api.post(
      endPoint + "deliverys",
      {
        search_query: deliverySearchQuery.value,
        states_id: delviery_states_id.value,
        citys_id: delivery_citys_id.value,
        per_page: 10,
        page: deliveryCurrentPage.value,
        details: true,
      },
      { skipLoading: true } as any,
    );
  } catch {
    if (!opts.silent) Toast.show("Could not retrieve Pending Delivery list", "warning");
    return;
  }
  if (response?.success == true) {
    // Detect new deliveries BEFORE we overwrite the local list so the diff
    // is computed against the previous poll's snapshot. First load doesn't
    // notify — it just seeds the seen-set so the cashier isn't greeted by
    // a wall of bubbles for the existing backlog.
    const incoming: any[] = response.data?.data ?? [];
    const incomingIds = new Set<number>(incoming.map((d: any) => Number(d.id)).filter(Boolean));
    if (deliveriesInitialised.value) {
      const fresh = incoming.filter((d: any) => !seenDeliveryIds.value.has(Number(d.id)));
      for (const d of fresh) {
        const customer = d.customer_name ?? "Customer";
        const docNo = d.related_document_no ?? d.document_no ?? "";
        notificationsStore.pushBubble({
          title: "New delivery to dispatch",
          body: docNo
            ? `${customer} — ${docNo}${d.full_address ? " · " + d.full_address : ""}`
            : `${customer}${d.full_address ? " · " + d.full_address : ""}`,
          category: "delivery",
          severity: "info",
          // Dedupe so a re-poll within the same cashier session can't
          // double-stack the same row's bubble.
          meta: { dedupe_key: `delivery:${d.id}`, delivery_id: d.id, delivery_uid: d.uid },
        });
      }
    }
    seenDeliveryIds.value = incomingIds;
    deliveriesInitialised.value = true;

    setPendingDeliveries(response);
  } else if (!opts.silent) {
    Toast.show("Could not retrieve Pending Delivery list", "warning");
  }
};

const handleCreateQuotation = async () => {
  let invoice: SalesDocument =
    pos.selectedPosInvoice?.invoice ?? ({} as SalesDocument);
  invoice.to_deliver = false ;//delivery_staff ? true : false;
  //invoice.delivery_staffs_id = delivery_staff;
  invoice.warehouses_id = props.posStation?.outlet?.warehouses_id ?? "";
  invoice.outlets_id = props.posStation?.outlet?.id ?? "";
  invoice.outlet_pos_stations_id = props.posStation?.id ?? "";
  invoice.price_lists_id = props.posStation?.outlet?.price_lists_id ?? "";
  invoice.sales_channels_id = props.posStation?.outlet?.sales_channels_id ?? "";

  invoice.share = true;

  if (
    !invoice.delivery_address_id &&
    invoice.customer?.addresses &&
    invoice.customer.addresses.length > 0
  ) {
    invoice.delivery_address_id = invoice.customer?.addresses?.[0]?.id ?? "";
  }

  invoice.cashier_dutys_id = props.cashierDuty ? props.cashierDuty.id : "";
  
  const response = await api.post(endPoint + "quotation",  invoice);
  if (response.success == true) {
    pos.voidInvoice();
    printPDFBlob(response.data)
  } else {
    Toast.show("Could not Create Quotation", "warning");
  }
}

const handleCustomerSelect = (customer: Customer) => {
  pos.setCustomer(customer);
  showCustomerList.value = false;
  pos.customerSearch = "";
};

//const discount = ref(0);

const deliveryCurrentPage = ref(1);
const deliveryTotalPages = ref(1);
const deliverySearchQuery = ref("");
const delivery_users = ref<User[]>([]);
const record_status = ref<RecordStatus[]>([]);
const delivery_citys_id = ref("");
const delviery_states_id = ref("");
const states = ref<{ id: any; name: any }[]>([]);
const citys = ref<{ id: any; name: any }[]>([]);

const setPendingDeliveries = (response: any) => {
  pendingDeliveries.value = response.data.data;
  deliveryTotalPages.value = response.data.total;
  if (response.lists && response.lists.status) {
    record_status.value = response.lists.status;
  }
  if (response.lists && response.lists.users)
    delivery_users.value = response.lists.users;
  let tab = tabs.value.find((tab) => tab.id == "deliveries");
  if (tab) {
    tab.badge = (pendingDeliveries.value.length ?? "").toString();
  }
  // States and cities used to be derived FROM the deliveries rows themselves
  // (filter unique state/city per delivery), which was a circular dependency:
  // an empty/filtered deliveries list emptied the dropdowns, making it
  // impossible to widen the filter again. Now both come from dedicated
  // master-data endpoints — see loadStates() + loadCities() below.
};

const assignDeliveryStaff = async (delivery: DeliveryTracking, user: User) => {
  const params = {
    id: delivery.uid,
    users_id: user.uid,
    page: deliveryCurrentPage.value,
    per_page: 25,
    search_query: deliverySearchQuery.value,
    filters: null,
  };
  const response = await api.post(
    (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + "assign-user",
    params
  );
  if (response.success == true) {
    setPendingDeliveries(response);
  }
};

const updateDeliveryStatus = async (
  delivery: DeliveryTracking,
  status: RecordStatus
) => {
  let attachment = null;
  let remarks = null;
  const confirmed = await MessageBox.confirm({
    title: "Update Delivery",
    message: status.require_attachment
      ? "Please upload the Delivery Confimration"
      : "Are you sure you want to update the delivery status?",
    confirmText: "Update as " + status.name,
    requireRemarks: status.require_remarks,
    requireAttachments: status.require_attachment,
    showRemarks: true,
    cancelText: "Cancel",
    type: "info",
  });
  if (confirmed) {
    attachment = confirmed.attachment;
    remarks = confirmed.remarks;
    if (status.require_attachment && !attachment) return;
    if (status.require_remarks && !remarks) return;
  } else {
    return;
  }

  const params = {
    id: delivery.uid,
    delivery_status_id: status.id,
    attachment: attachment,
    remarks: remarks,
    page: deliveryCurrentPage.value,
    per_page: 25,
    search_query: deliverySearchQuery.value,
    filters: null,
  };
  const response = await api.post(
    (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + "update-delivery",
    params
  );
  if (response.success == true) {
    setPendingDeliveries(response);
  }
};

const getPendingDeliveryStatus = (delivery: DeliveryTracking) => {
  let status = record_status.value.find(
    (e) => e.linked_index !== undefined && e.linked_index >= 1
  );
  let res = record_status.value.filter(
    (e) => e.id > (delivery.status_id ?? 0) && e.name !== "Delivery Assigned"
  );
  return status && !isEmpty(status)
    ? res.filter((e) => e.linked_index != status.linked_index)
    : res;
};

/* ───────────── Delivery details modal ─────────────
 * Clicking a delivery card opens a modal mounted with the canonical
 * staff-side DelieryDetails.vue panel — the same body the back-office
 * DeliveryTrackingsListView renders in its split view. We fetch the line
 * items separately because the deliveries list endpoint returns the
 * tracking row (customer / address / status / steps) but not the
 * itemised goods-delivery payload. Same `clients/.../items` endpoint
 * the back-office calls so the data shape matches exactly.
 *
 * Emitted events (`@delivery` and `@status`) are wired to the existing
 * `assignDeliveryStaff` / `updateDeliveryStatus` actions so a status
 * tick inside the modal updates the list the cashier returns to. */
const showDeliveryDetails = ref(false);
const selectedDelivery = ref<DeliveryTracking | null>(null);
const selectedDeliveryItems = ref<Item[]>([]);
const loadingDeliveryDetails = ref(false);

const openDeliveryDetails = async (delivery: DeliveryTracking) => {
  selectedDelivery.value = delivery;
  selectedDeliveryItems.value = [];
  showDeliveryDetails.value = true;
  loadingDeliveryDetails.value = true;
  try {
    const response: any = await api.post(
      "clients/revenue/sales/delivery-trackings/items",
      {
        id: delivery.uid,
        page: 1,
        per_page: 100,
        search_query: "",
        filters: null,
      },
      { skipLoading: true } as any,
    );
    if (response?.success && Array.isArray(response.data)) {
      selectedDeliveryItems.value = response.data;
    }
  } catch {
    /* Items list is decorative; the rest of the modal stays usable. */
  } finally {
    loadingDeliveryDetails.value = false;
  }
};

const closeDeliveryDetails = () => {
  showDeliveryDetails.value = false;
  selectedDelivery.value = null;
  selectedDeliveryItems.value = [];
};

// Refresh the selected-delivery's local copy with the latest list state
// after a status / assignment mutation, so the modal reflects the change
// without forcing the cashier to close + reopen. Falls back to the
// existing reference when the mutated row isn't in the freshly-loaded
// page (rare — pagination shift).
watch(pendingDeliveries, (list) => {
  if (!selectedDelivery.value) return;
  const match = list.find((d) => d.uid === selectedDelivery.value?.uid);
  if (match) selectedDelivery.value = match;
});

const showAddNewAddress = ref(false)

const searchCustomerInput = ref<HTMLInputElement | null>(null);
const focusCustomerSearch = () => {
  searchCustomerInput.value?.focus();
};

/**
 * Deliveries polling — only meaningful on shop-outlet POS stations
 * (restaurants don't have a deliveries surface). Initial fetch is loud
 * (Toast on failure) so a misconfigured tenant sees the error; subsequent
 * polls run silent so a transient hiccup mid-shift doesn't spam the
 * cashier. The 5-min cadence matches the user's product spec — new
 * deliveries surface as bottom-right bubbles via `pushBubble` (see the
 * detection loop inside `getPendingDeliveries`).
 */
let deliveriesPollTimer: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  window.addEventListener("keydown", handleKeyPress, true);
  if (props.posStation?.outlet?.type === "Shop") {
    loadStates();
    getPendingDeliveries();
    deliveriesPollTimer = setInterval(() => {
      getPendingDeliveries({ silent: true });
    }, 300000); // 5 minutes
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", handleKeyPress);
  if (deliveriesPollTimer) {
    clearInterval(deliveriesPollTimer);
    deliveriesPollTimer = null;
  }
});

const handleKeyPress = (event: KeyboardEvent) => {
  if (event.key === "F3") {
    event.preventDefault();
    showCustomerList.value = true;
  } else if (event.key === "F4") {
    event.preventDefault();
    focusCustomerSearch();
  } else if (event.key === "F12") {
    event.preventDefault();
    openCheckout();
  } else if (event.key === "F11") {
    event.preventDefault();
   createRestaurantOrder
  } else if (event.key === "Escape") {
    event.preventDefault();
    restaurantBack();
  }
};

const customerCreated = (address: any[]) => {
  pos.selectedPosInvoice?.invoice?.customer && (pos.selectedPosInvoice.invoice.customer.addresses = address);
}

const createRestaurantOrder = async () => {
  const params = {
    outlets_id: props.posStation?.outlet?.uid ?? "",
    outlet_pos_stations_id: props.posStation?.uid ?? "",
    tables_id: pos.selectedTable?.uid ?? "",
    orders_id: pos.selectedTable?.orders ? pos.selectedTable?.orders.uid : "",
    items: pos.selectedPosInvoice?.invoice.items ?? [],
    customers_id: pos.selectedPosInvoice?.invoice.customer?.uid ?? "",
  };
  const response = await api.post(
    (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + "order",
    params
  );
  if (response.success == true) {
    emit("order-created", response.data);
  }
}


const viewBill = () => {
  emit("view-bill", pos.selectedTable!);
}

const restaurantBack = () => {
  emit('back');
}

watch(
  () => pos.showCheckoutModal,
  () => {
    if (pos.showCheckoutModal) {
      window.removeEventListener("keydown", handleKeyPress);
    } else {
      window.window.addEventListener("keydown", handleKeyPress, true);
    }
  }
);

const customerMode = ref<"create" | "edit">("create");
const editCustomer = ref<Customer | null>(null);
const handleEditSelect = (customer: Customer) => {
  editCustomer.value = customer;
  customerMode.value = "edit";
  showCustomerList.value = false;
  showCustomerForm.value = true;
  pos.customerSearch = "";
};

</script>

<template>
  <div class="h-full flex flex-col">
    <!-- Customer Search -->
    <div class="p-4 border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center gap-2">
        <div class="relative flex-1">
          <IconComponent
            name="UserRoundSearch"
            class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            v-model="pos.customerSearch"
            ref="searchCustomerInput"
            type="search"
            placeholder="Search customer... (F4)"
            @keyup.enter="handleCustomerSearch"
            class="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
          />
        </div>
        <button
          @click="showCustomerList = true"
          class="p-2 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <IconComponent name="FileUser" class="w-5 h-5" />
        </button>
      </div>

      <!-- Selected Customer -->
      <div
        v-if="
          pos.selectedPosInvoice?.invoice.customer &&
          pos.selectedPosInvoice?.invoice.customers_id
        "
        class="mt-2 p-2 bg-blue-50 dark:bg-blue-900/50 rounded-lg"
      >
        <div class="flex justify-between items-start">
          <div>
            <h4 class="font-medium text-gray-900 dark:text-white">
              {{ pos.selectedPosInvoice?.invoice.customer.name }}
            </h4>
          </div>
          <div>
            <button
            @click="handleEditSelect(pos.selectedPosInvoice?.invoice.customer)"
            class="text-orange-400 hover:text-orange-500 dark:hover:text-orange-300"
          >
            <IconComponent name="Pencil" />
          </button>
          <button
            @click="pos.clearCustomer()"
            class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <IconComponent name="Trash2" />
          </button>
          </div>
        </div>
        <div class="md:grid md:grid-cols-3 md:gap-4">
                <div class="text-sm text-gray-600 dark:text-gray-400">
                  {{ pos.selectedPosInvoice?.invoice.customer.mobile }}
                </div>
                <div class="text-neutral-600 text-sm font-semibold text-right col-span-2">
                  <span
                    v-if="Number(pos.selectedPosInvoice?.invoice.customer?.credit_limit) - Number(pos.selectedPosInvoice?.invoice.customer?.outstanding) < 0"
                    class="inline-block px-2 py-1 rounded-full bg-red-100 text-red-700"
                  >
                    Credit Balance: {{ formatNumber(Number(pos.selectedPosInvoice?.invoice.customer?.credit_limit) - Number(pos.selectedPosInvoice?.invoice.customer?.outstanding)) }}
                  </span>
                  <span
                    v-else
                    class="inline-block px-2 py-1 rounded-full bg-blue-100 text-blue-700"
                  >
                    Credit Balance: {{ formatNumber(Number(pos.selectedPosInvoice?.invoice.customer?.credit_limit) - Number(pos.selectedPosInvoice?.invoice.customer?.outstanding)) }}
                  </span>
                </div>
              </div>
        <div class="flex mt-2  justify-between items-start">
            <IconComponent name="House" />
            <p class="text-sm text-gray-600 dark:text-gray-400 ml-1 mt-1">
              <span
              v-if="pos.selectedPosInvoice?.invoice?.customer?.selectedAddress"
              >
              {{
                pos.selectedPosInvoice?.invoice.customer.selectedAddress?.full_address ?? ""
              }}
              </span>
            </p>
            <DropDownButton :hide-arrow="true" v-if="(pos.selectedPosInvoice?.invoice?.customer?.addresses?.length ?? 0) > 1" icon="EllipsisVertical">
              <Buttons v-for="address in pos.selectedPosInvoice?.invoice?.customer?.addresses" :key="address.id" @click="pos.setCustomerAddress(address)" 
              :caption="address.full_address" class-name="text-left border-b" />
            </DropDownButton>
            <button
              @click="showAddNewAddress = true"
              class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <IconComponent name="HousePlus" />
            </button>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="border-b border-gray-200 dark:border-gray-700">
      <div class="flex">
        <template v-for="tab in tabs" :key="tab.id">
        <button
          :key="tab.id"
          v-if="tab.visible"
          @click="activeTab = tab.id"
          class="flex-1 px-4 py-3 text-sm font-medium text-center relative"
          :class="[
            activeTab === tab.id
              ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300',
          ]"
        >
          <div class="flex flex-col items-center">
            <IconComponent :name="tab.icon" class="block text-lg mb-1" />
            {{ tab.label }}
          </div>
          <span
            v-if="tab.badge"
            class="absolute top-2 right-2 min-w-[20px] h-5 flex items-center justify-center px-1 text-xs font-medium text-white bg-red-500 rounded-full"
          >
            {{ tab.badge }}
          </span>
        </button>
        </template>
      </div>
    </div>

    <!-- Content Area -->
    <div class="flex-1 flex flex-col min-h-0">
      <!-- Scrollable Content -->
      <div class="flex-1 overflow-y-auto">
        <!-- Order Tab -->
        <div v-if="activeTab === 'order'" class="p-4">
          <div
            v-if="pos.selectedPosInvoice?.invoice.items.length === 0"
            class="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400"
          >
            <span class="text-4xl mb-2">🛒</span>
            <p>Cart is empty</p>
          </div>
          <div v-else class="space-y-2">
            <template
              v-for="item in pos.selectedPosInvoice?.invoice.items"
              :key="item.id"
            >
              <div
                class="flex items-center gap-4 bg-gray-50 dark:bg-gray-900 p-3 rounded-lg relative"
                @mouseenter="item.isHovered = true"
                @mouseleave="item.isHovered = false"
                tabindex="0"
              >
                <div
                  class="w-10 h-10 flex items-center justify-center text-2xl flex-shrink-0"
                >
                  <AsyncImage
                    :src="item.photo"
                    :alt="item.name"
                    className="w-10 h-10 object-cover rounded-lg"
                  />
                </div>
                <div class="flex-1 min-w-0">
                  <div>
                    <h4
                      class="font-medium text-gray-900 dark:text-white truncate"
                    >
                      {{ item.name }}
                    </h4>
                  </div>
                  <div class="grid grid-cols-4 gap-2 flex-shrink-0">
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      {{ formatNumber(item.price) }}
                    </p>
                    <NumberInput
                        v-model="item.quantity"
                        @input="pos.updateQuantity(item, item.quantity)"
                        @change="pos.updateQuantity(item, item.quantity)"
                        class="w-full col-span-2 text-right"
                        size="sm"
                        :show-controls="true" />
                    <div class="text-right min-w-[80px]">
                      {{ formatNumber(item.net_total ?? 0) }}
                    </div>
                    <div class="col-span-3" v-if="item.isExpanded">
                      <div class="grid grid-cols-2">
                        <div>
                          <NumberInput
                            v-model="item.quantity"
                            @input="pos.updateQuantity(item, item.quantity)"
                            @change="pos.updateQuantity(item, item.quantity)"
                            class="w-10 text-right"
                            size="sm"
                            max-width="100px"
                            :show-controls="false"
                          />
                          <p
                            class="text-xs text-gray-500 dark:text-gray-400 mt-1"
                          >
                            Quantity
                          </p>
                        </div>
                        <div>
                          <NumberInput
                            v-model="item.discount_p"
                            @input="pos.updateLineTotal(item)"
                            class="w-10 text-right"
                            size="sm"
                            max-width="100px"
                            :show-controls="false"
                          />
                          <p
                            class="text-xs text-gray-500 dark:text-gray-400 mt-1"
                          >
                            Discount %
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  @click="item.isExpanded = !item.isExpanded"
                  ref="expand-button"
                  class="absolute top-2 right-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <IconComponent
                    :name="
                      item.isExpanded ? 'CircleArrowUp' : 'CircleArrowDown'
                    "
                    class="text-red"
                  />
                </button>
                <button
                  v-show="item.isHovered"
                  @click="pos.removeItem(item)"
                  ref="remove-button"
                  class="absolute top-2 right-8 text-gray-400 hover:text-gray-500 dark:hover:text-red-500"
                >
                  <IconComponent name="Trash2" class="text-red" />
                </button>
              </div>
            </template>
          </div>
        </div>

        <!-- Pending Deliveries Tab -->
        <div v-else-if="activeTab === 'deliveries'" class="p-4">
          <div class="space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <SearchSelect
                label="State"
                @select="handleStateChanged"
                v-model="delviery_states_id"
                :options="states"
                :remote="false"
              />
              <SearchSelect
                label="City"
                @select="handleCityChanged"
                v-model="delivery_citys_id"
                :options="citys"
                :remote="false"
              />
            </div>
            <div
              v-for="delivery in pendingDeliveries"
              :key="delivery.id"
              role="button"
              tabindex="0"
              @click="openDeliveryDetails(delivery)"
              @keydown.enter.prevent="openDeliveryDetails(delivery)"
              @keydown.space.prevent="openDeliveryDetails(delivery)"
              class="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:border-blue-400 hover:shadow-md transition-shadow"
              :title="'View delivery details: ' + (delivery.related_document_no || delivery.customer_name || '')"
            >
              <div class="flex justify-between items-start mb-2">
                <div>
                  <h4 class="font-medium text-gray-900 dark:text-white">
                    {{ delivery.related_document_no }}
                  </h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    {{ delivery.customer_name }}
                  </p>
                </div>
                <div class="text-right">
                  <span
                    class="px-2 py-1 text-xs font-medium rounded-full"
                    :style="{ backgroundColor: delivery.status_color }"
                  >
                    {{ delivery.status }}
                  </span>
                  <br />
                  <!-- Stop click bubbling so tapping the phone number doesn't
                       also pop the details modal. -->
                  <a
                    :href="'tel:' + delivery.customer_mobile"
                    @click.stop
                  >{{ delivery.customer_mobile }}</a>
                </div>
              </div>
              <div
                class="flex justify-between text-sm items-center mt-2 overflow-hidden text-ellipsis whitespace-nowrap"
              >
                {{ delivery.full_address }}
              </div>
              <!-- Inline quick-actions row — each interactive control gets
                   `@click.stop` (and a wrapper `@click.stop` around the
                   dropdown trigger) so a tap on a status / assign button
                   doesn't ALSO trip the card-level open-details handler. -->
              <div class="flex justify-between items-center mt-2" @click.stop>
                <span class="text-sm text-gray-500 dark:text-gray-400">
                  <div
                    class="flex items-center gap-2"
                    >
                  <div
                    class="flex items-center gap-2"
                    >
                    <DropDownButton
                      :hideArrow="true"
                    >
                      <button
                        @click.stop="updateDeliveryStatus(delivery, status)"
                        v-for="status in getPendingDeliveryStatus(delivery)"
                        class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <IconComponent :name="status.icon_css" />
                        {{ status.name }}
                      </button>
                    </DropDownButton>
                    <template v-if="!isEmpty(delivery.assigned_to_user)">
                    <IconComponent name="UsersRound" />
                    <span>{{ delivery.assigned_to_user }}</span>
                    </template>
                  </div>
                  <DropDownButton :hideArrow="true" icon="UsersRound" v-if="isEmpty(delivery.assigned_to_user)">
                    <button
                      @click.stop="assignDeliveryStaff(delivery, user)"
                      v-for="user in delivery_users"
                      class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      {{ user.name }}
                    </button>
                  </DropDownButton>
                  </div>
                </span>
                <span class="font-medium text-gray-900 dark:text-white">
                  {{ delivery.delivery_date }}
                </span>
              </div>
            </div>
          </div>
          <div>
            <DataTablePagination :current-page="deliveryCurrentPage" :total-pages="deliveryTotalPages" @pageChange="deliveryPageChange" />
          </div>
        </div>

        <!-- Notifications Tab — live inbox driven by the same Pinia store
             as the header bell + drawer + /notifications page. -->
        <div v-else-if="activeTab === 'notifications'" class="p-4">
          <div class="flex items-center justify-between mb-3">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              <span v-if="notificationsStore.unreadCount > 0" class="font-medium text-red-500">
                {{ notificationsStore.unreadCount }} unread
              </span>
              <span v-else>All caught up</span>
            </p>
            <div class="flex items-center gap-2">
              <button
                v-if="notificationsStore.unreadCount > 0"
                @click="notificationsStore.markAllRead()"
                class="text-xs text-blue-600 hover:underline"
              >
                Mark all read
              </button>
              <button
                @click="notificationsStore.load()"
                :disabled="notificationsStore.loading"
                class="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 disabled:opacity-50"
                :title="notificationsStore.loading ? 'Refreshing…' : 'Refresh'"
              >
                Refresh
              </button>
            </div>
          </div>

          <div
            v-if="notifications.length === 0"
            class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center text-sm text-gray-500 dark:text-gray-400"
          >
            <div class="text-2xl mb-1">🔔</div>
            No notifications yet.
          </div>

          <div v-else class="space-y-3">
            <div
              v-for="n in notifications"
              :key="n.id"
              @click="openNotification(n)"
              class="group bg-white dark:bg-gray-900 p-4 rounded-lg border cursor-pointer transition-colors"
              :class="!n.read_at
                ? 'border-blue-200 dark:border-blue-700 bg-blue-50/40 dark:bg-blue-950/20 hover:bg-blue-50 dark:hover:bg-blue-950/40'
                : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/40'"
            >
              <div class="flex gap-4">
                <div
                  class="w-10 h-10 rounded-full flex items-center justify-center text-xl flex-shrink-0"
                  :class="!n.read_at
                    ? 'bg-blue-100 dark:bg-blue-900/50'
                    : 'bg-gray-100 dark:bg-gray-800'"
                >
                  {{ notificationIcon(n.severity) }}
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-1 flex-wrap">
                    <h4
                      class="text-gray-900 dark:text-white truncate"
                      :class="!n.read_at ? 'font-semibold' : 'font-medium'"
                    >
                      {{ n.title }}
                    </h4>
                    <span
                      v-if="!n.read_at"
                      class="inline-block w-2 h-2 rounded-full bg-red-500 flex-shrink-0"
                      title="Unread"
                    />
                    <span
                      v-if="n.category"
                      class="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 uppercase tracking-wide text-gray-600 dark:text-gray-300"
                    >
                      {{ n.category }}
                    </span>
                  </div>
                  <p v-if="n.body" class="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                    {{ n.body }}
                  </p>
                  <div class="flex items-center justify-between mt-1">
                    <p class="text-xs text-gray-400 dark:text-gray-500">
                      {{ notificationTimeAgo(n.created_at) }}
                    </p>
                    <button
                      @click.stop="notificationsStore.dismiss(n.id)"
                      class="text-xs text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Dismiss"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Fixed Bottom Section -->
      <div
        v-if="activeTab === 'order'"
        class="mt-auto border-t border-gray-200 dark:border-gray-700"
      >
        <!-- Order Summary -->
        <div class="p-4 space-y-2">
          <!--
            Free-line promo hint: surfaced from posStore.previewPromotion()
            after the cashier clicks Checkout. Tells them which additional
            units to physically hand over (the customer is NOT charged for
            them; the server adds the row at price=0 on save and routes the
            cost to the promo's expense account).
          -->
          <div
            v-if="freeLines.length > 0"
            class="rounded-md bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 p-2 text-xs text-emerald-800 dark:text-emerald-200 space-y-1"
          >
            <div class="font-semibold uppercase tracking-wide text-[10px]">
              Free items (give to customer)
            </div>
            <div
              v-for="(fl, idx) in freeLines"
              :key="idx"
              class="flex justify-between"
            >
              <span>+ {{ fl.qty }} × item #{{ fl.items_id }}</span>
              <span class="opacity-70">{{ fl.promo_name }}</span>
            </div>
          </div>
          <!--
            Multi-item giveaway pool: when a free-line promo has >1 eligible
            item, expose a "Choose" button per promo so the cashier can pick
            which freebie (or split the qty across multiple items). The
            selection is persisted on inv.free_lines_override and submitted
            with the invoice.
          -->
          <div
            v-for="pool in choosablePools"
            :key="pool.promo_uid"
            class="flex items-center justify-between rounded-md bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 p-2 text-xs text-amber-800 dark:text-amber-200"
          >
            <div class="space-y-0.5">
              <div class="font-semibold uppercase tracking-wide text-[10px]">
                Choose free items — {{ pool.promo_name }}
              </div>
              <div class="opacity-80">
                {{ pool.free_total_qty }} free unit(s) · {{ pool.items.length }}
                eligible item(s)
              </div>
            </div>
            <button
              @click="openChooser(pool)"
              class="px-3 py-1.5 text-xs font-medium text-white bg-amber-600 rounded hover:bg-amber-700"
            >
              Choose
            </button>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-gray-600 dark:text-gray-400">Subtotal</span>
            <span class="font-medium text-gray-900 dark:text-white">{{
              formatCurrency(pos.sub_total ?? 0)
            }}</span>
          </div>
          <div class="flex justify-between items-center text-sm">
            <span class="text-gray-600 dark:text-gray-400">Discount</span>
            <NumberInput
              v-model="pos.discount"
              class="w-10 text-right"
              :readonly="!posStation?.outlet?.max_discount || !posStation?.outlet?.max_discount == null || posStation?.outlet?.max_discount <= 0"
              size="sm"
              max-width="100px"
              :show-controls="false"
              @input="pos.applyDiscount(pos.discount)"
            />
            <span class="font-medium text-gray-900 dark:text-white"
              >-{{ formatCurrency(pos.discount_amount ?? 0) }}</span
            >
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-gray-600 dark:text-gray-400">Before Tax</span>
            <span class="font-medium text-gray-900 dark:text-white">{{
              formatCurrency(pos.value_before_tax ?? 0)
            }}</span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-gray-600 dark:text-gray-400">Tax</span>
            <span class="font-medium text-gray-900 dark:text-white">{{
              formatCurrency(pos.tax_amount ?? 0)
            }}</span>
          </div>
          <div
            class="flex justify-between text-lg font-semibold pt-2 border-t border-gray-200 dark:border-gray-700"
          >
            <span class="text-gray-900 dark:text-white">Total</span>
            <span class="text-gray-900 dark:text-white">{{
              formatCurrency(pos.net_total ?? 0)
            }}</span>
          </div>
        </div>

        <!-- Checkout Button -->
        <div class="pl-4 pr-4 bg-gray-50 dark:bg-gray-900 flex gap-2">
          <button
            @click="createRestaurantOrder"
            class="w-full py-3 px-4 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="pos.selectedPosInvoice?.invoice.items.length === 0"
          >
            Order (F11)
          </button>
          <button
            @click="viewBill"
            v-if="posStation?.outlet?.type == 'Restaurant'"
            class="w-full py-3 px-4 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="!pos.selectedTable?.orders"
          >
            Bill (F12)
          </button>
          <button
            @click="openCheckout"
            v-else
            class="w-full py-3 px-4 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="pos.selectedPosInvoice?.invoice.items.length === 0"
          >
            Checkout (F12)
          </button>
          <button
            @click="restaurantBack"
            class="w-full py-3 px-4 bg-orange-300 text-black dark:text-white text-sm font-medium rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Back (Esc)
          </button>
        <DropDownButton :hideArrow="true" icon="FileText" text="Select" v-if="posStation?.outlet?.type == 'Shop'"
        className="w-full py-3 px-4 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed" 
        :disabled="pos.selectedPosInvoice?.invoice.items.length === 0">
            <Buttons
              class="w-full px-1 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              :disabled="!pos.selectedPosInvoice?.invoice.customer"
               @click="handleCreateQuotation"
              caption="Create Quotation"
            />
          </DropDownButton>
        </div>
      </div>
    </div>

    <!-- Customer List Dialog -->
    <CustomerSearchDialog
      v-if="showCustomerList"
      :customers="customerList"
      @select="handleCustomerSelect"
      @edit="handleEditSelect"
      @close="showCustomerList = false"
    />

    <!-- Checkout Modal -->
    <CheckoutModal
      v-if="pos.showCheckoutModal"
      :pos-station="posStation"
      :payment_types="payment_types"
      :cashierDuty="cashierDuty"
      :salesReps="props.salesReps"
      @getDeliverys="refreshDeliverys"
      :delivery_staffs="delivery_staffs"
      @new="$emit('new')"
      :total="pos.net_total ?? 0"
      @close="pos.showCheckoutModal = false"
    />

    <FreeItemChooserModal
      v-if="activeChooserPool"
      :pool="activeChooserPool"
      :preselected="freeLinesOverride"
      @apply="applyChooser"
      @close="closeChooser"
    />

    <!-- Delivery Details Modal — opens when a delivery card is clicked.
         Body delegates to the existing staff-side DeliveryDetails panel so
         the POS surface stays in lockstep with what the back office sees
         (customer info, items, remarks, action bar, full status timeline).
         Backdrop click + X button + ESC all close. Action emits route to
         the existing assignDeliveryStaff / updateDeliveryStatus actions so
         a status tick or driver assignment inside the modal updates the
         list the cashier sees on close. -->
    <div
      v-if="showDeliveryDetails && selectedDelivery"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[120] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delivery-details-title"
      @click.self="closeDeliveryDetails"
      @keydown.esc="closeDeliveryDetails"
      tabindex="-1"
    >
      <div
        class="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
        @click.stop
      >
        <!-- Modal header -->
        <header class="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div class="flex items-center gap-2 min-w-0">
            <IconComponent name="Truck" class="text-blue-600 flex-shrink-0" />
            <div class="min-w-0">
              <h3
                id="delivery-details-title"
                class="font-semibold text-gray-900 dark:text-white truncate"
              >
                Delivery {{ selectedDelivery.related_document_no || selectedDelivery.uid }}
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-400 truncate">
                {{ selectedDelivery.customer_name }}
                <span v-if="selectedDelivery.delivery_date">
                  · {{ selectedDelivery.delivery_date }}
                </span>
              </p>
            </div>
          </div>
          <button
            type="button"
            @click="closeDeliveryDetails"
            class="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
            aria-label="Close delivery details"
          >
            <IconComponent name="X" :size="18" />
          </button>
        </header>

        <!-- Modal body — DeliveryDetails handles the rich render; we just
             provide layout + a tiny loading indicator while items load. -->
        <div class="flex-1 overflow-y-auto p-5 space-y-4">
          <div
            v-if="loadingDeliveryDetails"
            class="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2"
          >
            <IconComponent name="Loader2" :size="14" class="animate-spin" />
            Loading items…
          </div>
          <DeliveryDetails
            :selected-delivery="selectedDelivery"
            :record-status="record_status"
            :items="selectedDeliveryItems"
            :show-steps="false"
            :pending="getPendingDeliveryStatus(selectedDelivery)"
            :delivery-users="delivery_users"
            @delivery="assignDeliveryStaff"
            @status="updateDeliveryStatus"
          />
        </div>
      </div>
    </div>

    <CustomersForm
      v-if="showCustomerForm"
      end-point="/clients/revenue/master-data/customers/"
      :ignore-permission="true"
      :minimized="true"
      :data="editCustomer"
      :listCreateName="pos.customerSearch"
      title="Customers"
      :mode="customerMode"
      @save="handleCustomerCreated"
      @close="showCustomerForm = !showCustomerForm"
    />

    <CustomerAddressForm v-if="showAddNewAddress" @close="showAddNewAddress = false" title="New Address" mode="create"
    :posStation="posStation" @save="customerCreated"
    end-point="/clients/revenue/master-data/customers/" :parent="pos.selectedPosInvoice?.invoice.customer" :ignore-permission="true" />
  </div>
</template>
